import React from 'react';
import { Box, ButtonBase, Paper, Typography } from '@mui/material';
import StudentPhoto from '../../components/StudentPhoto/StudentPhoto';
import { AttendanceStatus, RollCall } from '../../services/attendanceService';
import { StatusChip, STATUS_META } from './attendanceUi';

interface Props {
  students: RollCall['students'];
  statuses: Record<number, AttendanceStatus | null>;
  onChange: (userId: number, status: AttendanceStatus) => void;
  canRecord: boolean;
  isAdmin: boolean;
}

// Appel en mode trombinoscope : un clic sur la carte fait défiler le statut
// (non saisi → présent·e → absent·e → retard [→ justifiée pour l'admin] → présent·e…).
// Usage typique : « Non saisi·es → présent·es », puis un clic sur chaque absent·e.
const TrombiRollCall: React.FC<Props> = ({ students, statuses, onChange, canRecord, isAdmin }) => {
  const ORDER: AttendanceStatus[] = isAdmin ? ['present', 'absent', 'late', 'excused'] : ['present', 'absent', 'late'];

  const next = (current: AttendanceStatus | null): AttendanceStatus => {
    if (!current) return 'present';
    const index = ORDER.indexOf(current);
    return ORDER[(index + 1) % ORDER.length];
  };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 1.5 }}>
      {students.map(s => {
        const status = statuses[s.id] ?? null;
        // Justification posée par l'admin : verrouillée pour les enseignant·es (contrôlé aussi par l'API)
        const locked = !canRecord || (!isAdmin && s.status === 'excused');
        const color = status ? `${STATUS_META[status].color}.main` : 'divider';
        return (
          <Paper key={s.id} variant="outlined" sx={{ borderWidth: 3, borderColor: color, opacity: status === 'absent' ? 0.75 : 1 }}>
            <ButtonBase disabled={locked} onClick={() => onChange(s.id, next(status))}
              sx={{ width: '100%', p: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}
              title={locked ? 'Non modifiable' : 'Cliquer pour changer le statut'}>
              <StudentPhoto userId={s.id} lastname={s.lastname} firstname={s.firstname} size={96} variant="rounded" />
              <Typography variant="body2" fontWeight={600} align="center" sx={{ lineHeight: 1.2 }}>{s.lastname}</Typography>
              <Typography variant="caption" align="center" sx={{ lineHeight: 1.2 }}>{s.firstname}</Typography>
              <StatusChip status={status} />
            </ButtonBase>
          </Paper>
        );
      })}
      {students.length === 0 && <Typography>Aucun·e étudiant·e dans ce groupe.</Typography>}
    </Box>
  );
};

export default TrombiRollCall;
